import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { getGISServices } from './gisServicesAPI';

export const fetchGISServices = createAsyncThunk(
  'gisServices/fetchGISServices',
  async (orgName, { rejectWithValue }) => {
    try {
      const response = await getGISServices(orgName);
      return response.data;
    } catch (error) {
      if (!error.response) throw error;

      return rejectWithValue({
        message: error.response?.data?.detail,
        status: error.response?.status,
      });
    }
  },
);

// A mapping of the GIS service types
export const gisServiceTypes = {
  basemap: 0,
  supplemental: 1,
  geocode: 2,
  wms: 3,
  wmts: 4,
};

export const initialState = {
  activeGISServices: [],
  basemapService: {},
  error: null,
  geocodeService: {},
  gisIdentities: [],
  gisServices: [],
  lastToggledGISService: {},
  loading: false,
  numClearAllClicked: 0,
  supplementalServices: [],
  wmsServices: [],
  wmtsServices: [],
};

/**
 * Finds the GIS basemap service from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @returns Object
 */
export const findGISBasemapService = gisServices =>
  gisServices.find(
    gisService => gisService.service_type === gisServiceTypes.basemap,
  ) || initialState.basemapService;

/**
 * Filters the GIS supplemental services from a list of GIS services
 * @param {array} gisServices - list of GIS services
 * @returns Array
 */
export const filterGISSupplementalService = gisServices =>
  gisServices.filter(
    gisService => gisService.service_type === gisServiceTypes.supplemental,
  );

/**
 * Finds the GIS geocode service from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @returns Object
 */
export const findGISGeocodeService = gisServices =>
  gisServices.find(
    gisService => gisService.service_type === gisServiceTypes.geocode,
  ) || initialState.geocodeService;

/**
 * Filters the GIS services from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @param {String} serviceType - GIS service type, value should be wms or wmts
 * @returns Array
 */
export const filterServicesByType = (gisServices, serviceType) =>
  gisServices.filter(
    gisService => gisService.service_type === gisServiceTypes[serviceType],
  );

/**
 * Map the GIS identities from the `/gis-services` payload
 * @param {Array} gisIdentities - list of GIS identities
 */
export const mapGISIdentities = gisIdentities =>
  gisIdentities.map(identity => {
    const {
      arcgis_token: arcGISToken,
      gis_identity_url: gisIdentityUrl,
      gis_services: gisServices,
      token_expires_in: tokenExpiresIn,
    } = identity;

    return {
      arcGISToken,
      gisServices,
      gisIdentityUrl,
      tokenExpiresIn,
    };
  });

/**
 * Map the GIS services to a flat sorted array based on the display_order property from the
 * /gis-services payload.
 * @param {Array} gisIdentities - list of GIS identities
 */
export const mapGISServices = gisIdentities => {
  let gisServices = [];
  let layerIndex = 1;

  gisServices = gisIdentities
    .map(identity => {
      return identity.gis_services.map(service => {
        // Update GIS service display_order that has null value to 0 so that it can be sorted later.
        return {
          ...service,
          ...(service.display_order === null && { display_order: 0 }),
          token: identity?.arcgis_token || null,
        };
      });
    })
    .flat()
    .sort((a, b) => {
      // If the compared GIS services have the same display_order value, then sort by service_type.
      if (a.display_order === b.display_order) {
        return a.service_type - b.service_type;
      }

      return a.display_order - b.display_order;
    })
    .map(service => {
      // Add a new layerIndex property so that the layer can be loaded in a specific order.
      layerIndex = !service.display_order ? 1 : (layerIndex += 1);

      return {
        ...service,
        layerIndex,
      };
    });

  return gisServices;
};

/**
 * Filters the active GIS services when a match is found from current GIS services.
 * @param {Array} activeServices - List of active GIS services
 * @param {Array} currentServices - list of GIS Services returned from API request
 * @returns Array
 */
export const filterActiveGISServices = (activeServices, currentServices) =>
  activeServices.filter(active =>
    currentServices.find(current => active.id === current.id),
  );

const gisServicesSlice = createSlice({
  name: 'gisServices',
  initialState,
  reducers: {
    toggleGISService: (state, action) => {
      const service = action.payload;

      // When the map provider switches from Esri to Google, the toggled GIS service is an empty
      // object, don't push the empty object to the 'activeGISServices' array.
      if (Object.keys(service).length) {
        const isServiceActive = state.activeGISServices.filter(
          activeService => activeService.id === service.id,
        ).length;

        !isServiceActive
          ? state.activeGISServices.push(service)
          : (state.activeGISServices = state.activeGISServices.filter(
              activeService => activeService.id !== service.id,
            ));
      }

      state.lastToggledGISService = service;
    },
    clearAllGISServices: state => {
      state.activeGISServices = initialState.activeGISServices;
      state.lastToggledGISService = initialState.lastToggledGISService;
      state.numClearAllClicked += 1;
    },
  },
  extraReducers: {
    [fetchGISServices.pending]: state => {
      state.loading = true;
    },
    [fetchGISServices.fulfilled]: (state, action) => {
      state.error = initialState.error;
      state.loading = initialState.loading;

      const gisIdentities = action.payload?.gis_identities || [];

      if (gisIdentities.length) {
        const identities = mapGISIdentities(gisIdentities);

        state.gisIdentities = identities;

        const services = mapGISServices(gisIdentities);

        if (services.length) {
          state.gisServices = services;
          state.basemapService = findGISBasemapService(services);
          state.supplementalServices = filterServicesByType(
            services,
            'supplemental',
          );
          state.geocodeService = findGISGeocodeService(services);
          state.wmsServices = filterServicesByType(services, 'wms');
          state.wmtsServices = filterServicesByType(services, 'wmts');
          const activeGISServices = current(state.activeGISServices);

          // The activeGISServices state is a list of GIS services the user has enabled via the map
          // layers control on the Esri map, the values are persisted upon page refresh.
          // This condition filters the activeGISServices against gisServices and return an array of
          // GIS services that have matching ID values.
          if (activeGISServices.length) {
            state.activeGISServices = filterActiveGISServices(
              activeGISServices,
              services,
            );
          }
        }
      }
    },
    [fetchGISServices.rejected]: (state, action) => {
      if (action.payload) {
        const { message = '', status = null } = action.payload;

        state = {
          ...initialState,
          error: {
            message: message,
            status: status,
          },
        };

        return state;
      }
    },
  },
});

export const {
  toggleGISService,
  clearAllGISServices,
} = gisServicesSlice.actions;

// Note: Use of blacklist in both store.js and here results in all keys except
// the blacklisted key here being persisted.
const persistConfig = {
  key: 'gisServices',
  storage,
  blacklist: ['lastToggledGISService', 'numClearAllClicked'], // Do not persist on page refresh
};

const persistedReducer = persistReducer(
  persistConfig,
  gisServicesSlice.reducer,
);

export default persistedReducer;
